/* eslint-disable react/style-prop-object */
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Box from 'ui-box';

import {
  defaultColors,
  defaultFills,
  Heading,
  HTMLSelect,
  Icon,
  majorScale,
  minorScale,
  Spinner,
  Table,
} from '../../npaper';
import {
  IDashboardLeaderboard,
  IDashboardLeaderboardMonth,
  ITeamMember,
  useStores,
} from '../../stores';
import { UserThumbnail } from '../settings/UserThumbnail';

export const LeaderBoard = ({
  leaderboard,
}: {
  leaderboard: IDashboardLeaderboard;
}) => {
  const { authStore } = useStores();
  const currency = authStore.currentOrganisation
    ? authStore.currentOrganisation.default_currency
    : 'EUR';

  const [selectedMonth, setSelectedMonth] = useState<
    IDashboardLeaderboardMonth | undefined
  >(leaderboard.value.length > 0 ? leaderboard.value[0] : undefined);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Heading size={500} marginBottom={majorScale(1)}>
          {leaderboard.title}
        </Heading>
        <HTMLSelect
          options={leaderboard.value.map((month) => ({
            value: month.date,
            label: moment(month.date).format('YYYY MMMM'),
          }))}
          value={selectedMonth ? selectedMonth.date : undefined}
          onChange={(month) =>
            setSelectedMonth(leaderboard.value.find((m) => m.date === month))
          }
        />
      </Box>

      <Table large={true}>
        <thead>
          <tr>
            <th style={{ width: '20px' }}></th>
            <th style={{ width: '20px' }}></th>
            <th>
              <FormattedMessage defaultMessage="Person" />
            </th>
            <th className="right">
              <FormattedMessage defaultMessage="Revenue" />
            </th>
            <th className="right">
              <FormattedMessage defaultMessage="CM" />
            </th>
            <th className="right">
              <FormattedMessage defaultMessage="Goal (CM)" />
            </th>
            <th className="right">
              <FormattedMessage defaultMessage="Progress" />
            </th>
          </tr>
        </thead>
        <tbody>
          {selectedMonth &&
            selectedMonth.value
              .sort(
                (a, b) =>
                  // Sort by goal
                  b.contribution_margin / b.goal -
                  a.contribution_margin / a.goal
              )
              .map((item, i) => (
                <tr key={i}>
                  <td>
                    {i === 0 && (
                      <Icon icon="crown" color={defaultColors.amber[400]} />
                    )}
                  </td>
                  <td>
                    <strong>{i + 1}</strong>
                  </td>
                  <td>
                    <WrappedMember memberId={item.member} />
                  </td>
                  <td className="font-mono right">
                    <FormattedNumber
                      style="currency"
                      value={item.revenue}
                      currency={currency}
                      currencyDisplay="code"
                    />
                  </td>
                  <td className="font-mono right">
                    <FormattedNumber
                      style="currency"
                      value={item.contribution_margin}
                      currency={currency}
                      currencyDisplay="code"
                    />
                  </td>
                  <td className="font-mono right">
                    <FormattedNumber
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      value={item.goal}
                      currency={currency}
                      currencyDisplay="code"
                    />
                  </td>
                  <Box
                    is="td"
                    className="font-mono"
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {item.contribution_margin / item.goal >= 1 && (
                      <Icon
                        icon="tick"
                        color={defaultFills.success[500]}
                        marginRight={minorScale(1)}
                      />
                    )}
                    <FormattedNumber
                      style="percent"
                      value={item.contribution_margin / item.goal}
                    />
                  </Box>
                </tr>
              ))}
        </tbody>
      </Table>
    </Box>
  );
};

const WrappedMember = ({ memberId }: { memberId: number | string }) => {
  const { teamMemberStore } = useStores();
  const [member, setMember] = useState<ITeamMember>();
  useEffect(() => {
    let isSub = true;
    teamMemberStore
      .getItem(memberId)
      .then((member) => isSub && setMember(member));
    return () => {
      isSub = false;
    };
  }, [memberId, teamMemberStore]);

  if (!member) return <Spinner size={16} />;
  return <UserThumbnail user={member.user} showName />;
};
